import React, { useState } from 'react';
import { useTask } from '../contexts/TaskContext';
import { formatRecurLabel } from '../services/firebase';

const WEEKDAYS = [
  { value: 0, label: '日' },
  { value: 1, label: '月' },
  { value: 2, label: '火' },
  { value: 3, label: '水' },
  { value: 4, label: '木' },
  { value: 5, label: '金' },
  { value: 6, label: '土' },
];

const emptyForm = { title: '', type: 'daily', weekdays: [], dayOfMonth: 1 };

export default function RecurringTaskModal({ isOpen, onClose }) {
  const { recurringTasks, addRecurring, updateRecurring, deleteRecurring } = useTask();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  if (!isOpen) return null;

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const toggleWeekday = (value) => {
    setForm((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(value)
        ? prev.weekdays.filter((w) => w !== value)
        : [...prev.weekdays, value],
    }));
  };

  // 入力内容が有効か（毎週は曜日が1つ以上必要）
  const isValid =
    form.title.trim() &&
    (form.type !== 'weekly' || form.weekdays.length > 0);

  const buildPayload = () => {
    const base = { title: form.title.trim(), type: form.type };
    if (form.type === 'weekly') {
      return { ...base, weekdays: form.weekdays, dayOfMonth: null };
    }
    if (form.type === 'monthly') {
      return { ...base, weekdays: [], dayOfMonth: form.dayOfMonth };
    }
    return { ...base, weekdays: [], dayOfMonth: null };
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    const payload = buildPayload();
    if (editingId) {
      await updateRecurring(editingId, payload);
    } else {
      await addRecurring(payload);
    }
    resetForm();
  };

  const startEdit = (rec) => {
    setEditingId(rec.id);
    setForm({
      title: rec.title || '',
      type: rec.type || 'daily',
      weekdays: Array.isArray(rec.weekdays) ? rec.weekdays : [],
      dayOfMonth: rec.dayOfMonth || 1,
    });
  };

  const handleDelete = async (rec) => {
    if (window.confirm(`「${rec.title}」を削除しますか？`)) {
      if (editingId === rec.id) resetForm();
      await deleteRecurring(rec.id);
    }
  };

  const sorted = [...recurringTasks].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{'🔄'} 定期タスク</h2>
          <button className="modal-close" onClick={onClose} aria-label="閉じる">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {/* ===== 追加・編集フォーム ===== */}
          <div className="recur-form">
            <input
              type="text"
              className="task-add-input recur-title-input"
              placeholder="定期タスク名を入力..."
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            />

            <div className="recur-field-label">くりかえし</div>
            <div className="recur-type-row">
              {[
                { v: 'daily', l: '毎日' },
                { v: 'weekly', l: '毎週' },
                { v: 'monthly', l: '毎月' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  className={`recur-type-btn ${form.type === opt.v ? 'active' : ''}`}
                  onClick={() => setForm((p) => ({ ...p, type: opt.v }))}
                >
                  {opt.l}
                </button>
              ))}
            </div>

            {form.type === 'weekly' && (
              <div className="recur-weekday-row">
                {WEEKDAYS.map((w) => (
                  <button
                    key={w.value}
                    type="button"
                    className={`recur-weekday-btn ${form.weekdays.includes(w.value) ? 'active' : ''}`}
                    onClick={() => toggleWeekday(w.value)}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            )}

            {form.type === 'monthly' && (
              <div className="recur-monthday-field">
                <span>毎月</span>
                <select
                  className="recur-monthday-select"
                  value={form.dayOfMonth}
                  onChange={(e) => setForm((p) => ({ ...p, dayOfMonth: parseInt(e.target.value, 10) }))}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <span>日</span>
                <span className="recur-monthday-note">（無い月は月末）</span>
              </div>
            )}

            <div className="recur-form-actions">
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  キャンセル
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!isValid}
              >
                {editingId ? '更新する' : '追加する'}
              </button>
            </div>
          </div>

          {/* ===== 登録済み一覧 ===== */}
          <div className="recur-list-section">
            <h3 className="recur-list-title">登録済み（{sorted.length}件）</h3>
            {sorted.length === 0 ? (
              <p className="empty-note">定期タスクはまだありません</p>
            ) : (
              <ul className="recur-list">
                {sorted.map((rec) => (
                  <li key={rec.id} className={`recur-item ${editingId === rec.id ? 'editing' : ''}`}>
                    <div className="recur-item-main">
                      <span className="recur-item-title">{rec.title}</span>
                      <span className="recur-item-badge">{formatRecurLabel(rec)}</span>
                    </div>
                    <div className="recur-item-actions">
                      <button
                        type="button"
                        className="recur-icon-btn"
                        onClick={() => startEdit(rec)}
                        aria-label="編集"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="recur-icon-btn recur-icon-danger"
                        onClick={() => handleDelete(rec)}
                        aria-label="削除"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
